import UsageChangeIcon from '@acorn-portal/shared/src/assets/shared/icons/base/car-shield-tick-large.svg?react';
import AddressChangeIcon from '@acorn-portal/shared/src/assets/shared/icons/base/house-large.svg?react';
import DriverChangeIcon from '@acorn-portal/shared/src/assets/shared/icons/base/circled-people-large.svg?react';
import { Box, Collapse, Fade, Grid, Paper, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import LargeIconButton from '@acorn-portal/shared/src/components/buttons/LargeIconButton';
import LoadingModal from '@acorn-portal/shared/src/components/modal/LoadingModal';
import PageTitle from '@acorn-portal/shared/src/components/text/PageTitle';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import { FC, useEffect, useState, useCallback, useRef } from 'react';
import {
	setIsMtaFutureDate,
	setPolicyReference,
	setPolicyToken,
	setStartDate,
	clearMidTermAdjustmentDetails,
} from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { getLists } from '@acorn-portal/shared/src/store/referenceData.slice';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import DayPicker from '@acorn-portal/shared/src/components/datePicker/day-picker';
import { CalendarMonth, Schedule } from '@mui/icons-material';
import { GetMtaStartDateRange, getDateOnly } from '@acorn-portal/shared/src/pages/mta/utils';
import { useCheckVehicleIsMtaEligible } from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { Product } from '@acorn-portal/shared/src/services/interface/Shared';
import { add, formatISO } from 'date-fns';
import { getVehicleIconByProduct } from '@acorn-portal/shared/src/utils/constants/vehicleIcons';
import { useFutureDateTime } from '@acorn-portal/shared/src/hooks/useFutureTimeSelection';
import { setNextValidFutureDateTime } from '../../utils/formDateValueUtils';
import { isTelematics } from '../../utils';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';

const MakeAChangeHome: FC = () => {
	const [loading, setLoading] = useState(false);
	const [futureDatePicker, setFutureDatePicker] = useState(false);
	const dispatch = useAppDispatch();
	const lists = useAppSelector((state) => state.referenceData);
	const policy = useAppSelector((state) => state.fullPolicy);
	const [min, max] = GetMtaStartDateRange();
	const { track } = useTrack();

	const { isEligible } = useCheckVehicleIsMtaEligible();

	const { hour, setHour, minute, setMinute, error, errorMessage, mtaStartDate, setMtaStartDate, mtaDateTime } =
		useFutureDateTime(new Date(), futureDatePicker, { acceptMidnight: true });

	useEffect(() => {
		track(`Make A Change Page Viewed`);
	}, []);

	useEffect(() => {
		if (futureDatePicker) {
			const isoDate = formatISO(mtaDateTime);
			dispatch(setStartDate(isoDate));
		}
	}, [mtaDateTime, futureDatePicker]);

	const setFutureDated = (value: boolean) => {
		dispatch(setIsMtaFutureDate(value));
	};

	const setMtaDate = (date: Date) => {
		const isoDate = formatISO(date);
		dispatch(setStartDate(isoDate));
	};

	const setDefaultAdjustmentStartDate = () => {
		const now = new Date();
		const fifteenMinutesFromNow = add(now, { minutes: 15 });
		setMtaDate(fifteenMinutesFromNow);
	};

	useEffect(() => {
		dispatch(clearMidTermAdjustmentDetails());
	}, []);

	const isLoadingRefData = useRef(false);
	const fetchReferenceData = useCallback(async () => {
		if (isLoadingRefData.current) {
			return;
		}
		isLoadingRefData.current = true;
		dispatch(getLists()).finally(() => {
			isLoadingRefData.current = false;
		});
	}, [dispatch]);

	useEffect(() => {
		dispatch(setPolicyReference(policy.policyNumber));
		dispatch(setPolicyToken(policy.policyToken));
	}, [dispatch, policy.policyNumber, policy.policyToken]);

	useEffect(() => {
		if (lists.titlesList.length !== 0) return;

		setLoading(true);
		fetchReferenceData()
			.then(() => {
				setLoading(false);
			})
			.catch((e) => {
				console.error(e);
				setLoading(false);
			});
	}, [dispatch, fetchReferenceData, lists.titlesList.length]);

	const handleFutureDateToggleChange = (_event: React.MouseEvent<HTMLElement>, isFutureDatePickerSelected: boolean) => {
		// Default the date to now.
		if (!isFutureDatePickerSelected) {
			setDefaultAdjustmentStartDate();
		}
	};

	const updateMtaStartDateTime = () => {
		const { date, hour, minute } = setNextValidFutureDateTime(new Date(), 15);
		setHour(hour);
		setMinute(minute);
		setMtaStartDate(date);
		setMtaDate(date);
	};

	const isTaxi = policy.product === Product.TAXI;
	const isTelematicsOrMyPolicy = isTelematics(policy.product) || import.meta.env.VITE_AUTH_REALM === 'MyPolicy';
	const isStreetCover = policy.productCode === 'Street_Cover_PC';

	function getUsageText(): string {
		switch (true) {
			case isTaxi:
				return 'Change of Usage & Licencing Authority';
			case isTelematicsOrMyPolicy:
				return 'Change of Usage';
			case isStreetCover: {
				const isAnnual = policy.scheme === 'StreetCoverAnnual';

				return isAnnual ? 'Change of Usage & Mileage' : 'Change of Mileage';
			}
			default:
				return 'Change of Usage & Mileage';
		}
	}

	return (
		<Paper
			elevation={1}
			sx={{
				borderRadius: '8px',
			}}
		>
			<LoadingModal open={loading} message='Retrieving Details...' />
			<Fade in>
				<Box sx={{ margin: '1rem', textAlign: { xs: 'left', md: 'center' }, p: 1 }}>
					<PageTitle>Make a Change</PageTitle>
					<Typography sx={{ mx: { md: 6 } }}>
						It’s important you answer all questions fully and accurately. If you deliberately or carelessly make a
						misrepresentation, your cover could be invalidated, and any claims rejected or not fully paid.
					</Typography>
					<Box>
						<Typography
							sx={(theme) => ({
								[theme.breakpoints.up('md')]: { mx: 6 },
								mt: 2,
							})}
						>
							Select when you would like the change to come into effect.
						</Typography>
						<Box sx={{ padding: 1 }} />
						<Box sx={{ textAlign: 'center' }}>
							<ToggleButtonGroup
								color='primary'
								value={futureDatePicker}
								exclusive
								onChange={handleFutureDateToggleChange}
								aria-label='Change Start Date Options'
							>
								<ToggleButton
									value={false}
									onClick={() => {
										setFutureDatePicker(false);
										setFutureDated(false);
										updateMtaStartDateTime();
									}}
								>
									<Schedule sx={{ pr: 1 }} />
									Change Now
								</ToggleButton>
								<ToggleButton
									value
									onClick={() => {
										updateMtaStartDateTime();
										setFutureDatePicker(true);
									}}
								>
									<CalendarMonth sx={{ pr: 1 }} />
									Future Change
								</ToggleButton>
							</ToggleButtonGroup>
						</Box>

						<Collapse in={futureDatePicker}>
							<Typography sx={{ margin: '1rem' }}>Please select the day you want the change to start from</Typography>

							<DayPicker
								maxDate={max}
								minDate={min}
								time
								minute={minute}
								hour={hour}
								setHour={setHour}
								setMinute={setMinute}
								error={error}
								errorMessage={errorMessage}
								value={mtaStartDate ?? new Date()}
								onChange={(date) => {
									if (date) {
										const dateOnly = getDateOnly(date);
										setMtaStartDate(dateOnly);
										setMtaDate(dateOnly);
										setFutureDated(true);
									}
								}}
							/>
						</Collapse>
					</Box>
					<Typography sx={(theme) => ({ [theme.breakpoints.up('md')]: { mx: 6 }, mt: 2 })}>
						Please select the type of change below. If you wish to make multiple changes, please select the change you
						would like to start with.
					</Typography>
					<Box
						sx={{
							marginLeft: 'auto',
							marginRight: 'auto',
							display: 'inline-block',
							pt: 2,
						}}
					>
						<Grid container sx={{ maxWidth: '550px' }}>
							<Grid size={{ sm: 12, md: 6 }} width='100%'>
								<LargeIconButton
									href={ROUTE_PATHS.makeAChangeVehicle}
									Icon={getVehicleIconByProduct(policy.product, 'large')}
									text='Change of Vehicle'
									disabled={!isEligible || error}
								/>
							</Grid>
							<Grid size={{ sm: 12, md: 6 }} width='100%'>
								<LargeIconButton
									href={ROUTE_PATHS.makeAChangeAddress}
									Icon={<AddressChangeIcon />}
									text='Change of Address'
									disabled={error}
								/>
							</Grid>
							<Grid size={{ sm: 12, md: 6 }} width='100%'>
								<LargeIconButton
									href={ROUTE_PATHS.makeAChangeDrivers}
									Icon={<DriverChangeIcon />}
									text='Manage Drivers'
									disabled={error}
								/>
							</Grid>
							<Grid size={{ sm: 12, md: 6 }} width='100%'>
								<LargeIconButton
									href={isTaxi ? ROUTE_PATHS.makeAChangeUsageTaxi : ROUTE_PATHS.makeAChangeUsageAndMileage}
									Icon={<UsageChangeIcon />}
									text={getUsageText()}
									disabled={error}
								/>
							</Grid>
						</Grid>
					</Box>
				</Box>
			</Fade>
		</Paper>
	);
};

export default MakeAChangeHome;
